/**
 * 企业入驻申请（N13 / PRD §5.1.2 注册与入驻）
 *
 * 后端路由（router.go / handler/merchant.go）：
 *   POST /api/v1/merchants/applications        提交入驻申请（登录即可，需 merchant:apply 权限）
 *   GET  /api/v1/merchants/applications/mine   我的申请记录（含驳回原因，便于修改后重提）
 *   POST /api/v1/upload                        营业执照上传（biz=merchant_license，返回 COS url）
 *
 * 错误码（service/merchant.go 3601-3606）：
 *   3601 状态不允许 / 3602 申请不存在 / 3603 已审核不可重复
 *   3604 已有待审申请 / 3605 已具备企业资质 / 3606 资料不完整或格式不合法
 */
import { get, post, upload } from './http';

/** 入驻申请状态（与后端 model.MerchantAppStatusXxx 对齐） */
export const MERCHANT_APP_STATUS = {
  PENDING: 0, // 待审核
  APPROVED: 1, // 已通过
  REJECTED: 2, // 已驳回
} as const;

/** 状态中文名 */
export const MERCHANT_APP_STATUS_LABEL: Record<number, string> = {
  0: '待审核',
  1: '已通过',
  2: '已驳回',
};

/** 入驻申请记录 */
export interface MerchantApplication {
  id: number;
  user_id: number;
  username: string;
  enterprise_name: string;
  credit_code: string;
  license_url: string;
  legal_person: string;
  contact_name: string;
  contact_phone: string;
  business_scope: string;
  remark: string;
  /** 0-待审核 1-已通过 2-已驳回 */
  status: number;
  reject_reason: string;
  reviewer_id: number;
  reviewer_name: string;
  reviewed_at: string | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

/** 入驻申请提交参数（license_url 由上传接口先行换取） */
export interface MerchantApplicationParams {
  enterprise_name: string;
  credit_code: string;
  license_url: string;
  legal_person?: string;
  contact_name: string;
  contact_phone: string;
  business_scope?: string;
  remark?: string;
}

/** 上传结果（POST /upload） */
export interface UploadResult {
  url: string;
  object_key: string;
  storage: string;
  size: number;
}

/** 提交企业入驻申请 */
export async function submitMerchantApplication(
  params: MerchantApplicationParams,
): Promise<MerchantApplication> {
  return post<MerchantApplication>('/merchants/applications', params);
}

/** 我的入驻申请记录（倒序） */
export async function listMyMerchantApplications(): Promise<MerchantApplication[]> {
  return get<MerchantApplication[]>('/merchants/applications/mine');
}

/** 上传营业执照（走通用上传接口，biz 归类到 merchant_license） */
export async function uploadLicense(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('biz', 'merchant_license');
  return upload<UploadResult>('/upload', fd);
}

/** 统一社会信用代码校验（18 位，数字 + 大写字母，排除 I/O/S/V/Z） */
export const CREDIT_CODE_PATTERN = /^[0-9A-HJ-NPQRTUWXY]{18}$/;
